import {
    LazyFormulaExpression,
    LiveFormulaExpression,
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {IFormulaNode} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaDefinitions';
import {FormulaOps} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaOps';
import {FormulaType} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaType';
import {
    OhmParseInputResult,
    OhmParseResult
} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {IEvalInputOpContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/FormulaInputEvalOp';
import {IFormulaParseContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/buildAST/BuildFormulaAST';
import {IFormulaToken} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {FormulaString} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {area, mk} from '@mintdata/mint-kit/api/log/mk';
import {Dict, Grammar, Matcher, MatchResult, Node, Semantics} from 'ohm-js';

export type OhmNode = Node;

export interface IOhmOperation extends Dict
{
    readonly operationName:string;
}

export class OhmEngine
{
    private _grammar:Grammar;
    private _semantics:Semantics;
    private _matcher:Matcher;

    constructor(grammar:Grammar, semantics:Semantics)
    {
        this._grammar = grammar;
        this._matcher = grammar.matcher();

        this._semantics = semantics;
    }

    public parseFormula(parseContext:IFormulaParseContext, formula:FormulaString, startActionName:string):OhmParseResult
    {
        const operationName:string = FormulaOps.BuildFormulaAST.operationName;

        FormulaOps.BuildFormulaAST.setContext(parseContext);
        this._matcher.setInput(formula);

        const parseResult:MatchResult = this._matcher.match(startActionName);

        if( parseResult.failed() )
            return new OhmParseResult(false, null, FormulaType.RegularFormula, this._toErrorMessage(parseResult));

        const thisRef:Dict = this._semantics(parseResult);
        const ohmOperationFn:IOhmOperation = thisRef[operationName];

        const astRoot:IFormulaNode = ohmOperationFn.apply(thisRef);

        mk.debug(area.Formula, `Node tree for '${formula}'`, parseResult);
        mk.debug(area.Formula, `Parse tree for '${formula}'`, astRoot);

        if( astRoot instanceof LazyFormulaExpression )
            return new OhmParseResult(true, astRoot, FormulaType.LazyFormula, null);

        if( astRoot instanceof LiveFormulaExpression )
            return new OhmParseResult(true, astRoot, FormulaType.LiveFormula, null);

        return new OhmParseResult(true, astRoot, FormulaType.RegularFormula, null);
    }

    public parseInputFormula(parseContext:IEvalInputOpContext, formula:FormulaString, startActionName:string):OhmParseInputResult
    {
        const operationName:string = FormulaOps.EvalInput.operationName;

        FormulaOps.EvalInput.setContext(parseContext);

        return this.invoke(operationName, formula, startActionName);
    }

    public invoke(operationName:string, input:string, startActionName?:string):OhmParseInputResult
    {
        this._matcher.setInput(input);

        const parseResult:MatchResult = this._matcher.match(startActionName);

        if( parseResult.failed() )
            return new OhmParseInputResult(false, null, this._toErrorMessage(parseResult));

        const thisRef:Dict = this._semantics(parseResult);
        const ohmOperationFn:IOhmOperation = thisRef[operationName];

        const result:Array<IFormulaToken> | IFormulaToken = ohmOperationFn.apply(thisRef);

        return new OhmParseInputResult(true, result, null);
    }

    private _toErrorMessage(parseResult:MatchResult):string
    {
        const failurePosition = parseResult['getRightmostFailurePosition']();
        const failures:any[] = parseResult['getRightmostFailures']();

        let sb = `Parse failed:\n${parseResult.message}\n\nFailure position: ${failurePosition}`;

        if( failures )
        {
            const numFailures = failures.length;
            sb += ` count: ${numFailures} \n\n`;

            for( let i = 0; i < numFailures; i++ )
            {
                const failure = failures[i];

                sb += `${i}) `;

                if( failure.type === 'string' )
                    sb += `'${failure.text}' (${failure.type})`;
                else
                    sb += `${failure.text} (${failure.type})`;

                if( i < numFailures - 1 )
                    sb += `\n`;
            }
        }

        return sb;
    }
}
